import React, { useEffect } from 'react';
import { QueryProvider } from '@/providers/QueryProvider';
import { AppLayout } from '@/components/layout/AppLayout';
import { LoadingScreen } from '@/components/ui';
import { TelegramUtils } from '@/utils/telegram';
import { useAppStore, useTelegram } from '@/store';
import { useBootstrap } from '@/hooks/api';

// Screen imports
import SplashScreen from '@/screens/SplashScreen';
import GenderSelectScreen from '@/screens/GenderSelectScreen';
import ITChoiceScreen from '@/screens/ITChoiceScreen';
import LookingForScreen from '@/screens/LookingForScreen';
import OnboardingScreen from '@/screens/OnboardingScreen';
import ITQuestScreen from '@/screens/ITQuestScreen';
import SkillsVerificationScreen from '@/screens/SkillsVerificationScreen';
import UpdateSpecializationScreen from '@/screens/UpdateSpecializationScreen';
import FeedScreen from '@/screens/FeedScreen';
import AlternativeFeedScreen from '@/screens/AlternativeFeedScreen';
import MatchesScreen from '@/screens/MatchesScreen';
import ChatScreen from '@/screens/ChatScreen';
import ProfileScreen from '@/screens/ProfileScreen';
import EditProfileScreen from '@/screens/EditProfileScreen';
import SettingsScreen from '@/screens/SettingsScreen';
import BlockedUsersScreen from '@/screens/BlockedUsersScreen';
import FiltersScreen from '@/screens/FiltersScreen';

const AppRouter: React.FC = () => {
  const { activeScreen } = useAppStore(state => state.ui);
  const { isAuthenticated, onboarding, feedType } = useAppStore();

  // Bootstrap app data
  const { data: bootstrapData, isLoading: isBootstrapping, error } = useBootstrap({
    enabled: true, // Always enabled now
  });

  // Show loading screen during bootstrap
  if (isBootstrapping) {
    return <LoadingScreen message="Инициализация ITinder..." />;
  }

  // Show error if bootstrap failed
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-tg-bg p-6">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-tg-text mb-2">
            Ошибка инициализации
          </h2>
          <p className="text-tg-hint mb-4">
            Попробуйте обновить приложение
          </p>
          <button 
            onClick={() => window.location.reload()}
            className="btn-primary"
          >
            Обновить
          </button>
        </div>
      </div>
    );
  }

  // Route based on activeScreen (bootstrap sets the correct screen)
  if (!isAuthenticated || !onboarding.completed) {
    // Splash screen (shown during bootstrap)
    if (activeScreen === 'splash') {
      return <SplashScreen />;
    }
    
    // Gender selection
    if (activeScreen === 'gender-select') {
      return <GenderSelectScreen />;
    }
    
    // IT choice (for females)
    if (activeScreen === 'it-choice') {
      return <ITChoiceScreen />;
    }
    
    // Looking for preferences
    if (activeScreen === 'looking-for') {
      return <LookingForScreen />;
    }
    
    // IT Quest test
    if (activeScreen === 'it-quest') {
      return <ITQuestScreen />;
    }
    
    // Onboarding
    if (activeScreen === 'onboarding') {
      return <OnboardingScreen />;
    }
    
    // Fallback to splash
    return <SplashScreen />;
  }

  // Main app screens
  switch (activeScreen) {
    case 'feed':
    default:
      // Choose between card feed and vertical feed based on feedType
      return feedType === 'vertical' ? <AlternativeFeedScreen /> : <FeedScreen />;
    case 'filters':
      return <FiltersScreen />;
    case 'matches':
      return <MatchesScreen />;
    case 'chat':
      return <ChatScreen />;
    case 'profile':
      return <ProfileScreen />;
    case 'edit-profile':
      return <EditProfileScreen />;
    case 'settings':
      return <SettingsScreen />;
    case 'blocked-users':
      return <BlockedUsersScreen />;
    case 'skills-verification':
      return <SkillsVerificationScreen />;
    case 'update-specialization':
      return <UpdateSpecializationScreen />;
    case 'it-quest':
      return <ITQuestScreen />;
  }
};

const TelegramIntegration: React.FC = () => {
  const { 
    setTelegramWebApp, 
    setTelegramUser, 
    setTelegramTheme, 
    setTelegramReady 
  } = useTelegram();

  useEffect(() => {
    // Initialize Telegram WebApp
    const initTelegram = () => {
      const tg = TelegramUtils.webApp;
      
      if (tg) {
        // Set Telegram data in store
        setTelegramWebApp(tg);
        setTelegramUser(TelegramUtils.getUserInfo());
        setTelegramTheme(TelegramUtils.getThemeParams());
        setTelegramReady(true);

        // Configure initial UI
        TelegramUtils.configureBackButton(false);
        TelegramUtils.configureMainButton({
          visible: false,
        });

        console.log('✅ Telegram WebApp integration initialized');
      } else {
        console.log('ℹ️ Running outside Telegram WebApp (development mode)');
        setTelegramReady(true);
      }
    };

    // Initialize when component mounts
    initTelegram();

    // Listen for Telegram events
    const handleThemeChange = () => {
      setTelegramTheme(TelegramUtils.getThemeParams());
    };

    const handleViewportChange = () => {
      // Handle viewport changes (keyboard show/hide)
      console.log('Viewport changed:', TelegramUtils.webApp?.viewportHeight);
    };

    if (TelegramUtils.webApp) {
      TelegramUtils.webApp.onEvent('themeChanged', handleThemeChange);
      TelegramUtils.webApp.onEvent('viewportChanged', handleViewportChange);
    }

    // Cleanup
    return () => {
      if (TelegramUtils.webApp) {
        TelegramUtils.webApp.offEvent('themeChanged', handleThemeChange);
        TelegramUtils.webApp.offEvent('viewportChanged', handleViewportChange);
      }
    };
  }, [setTelegramWebApp, setTelegramUser, setTelegramTheme, setTelegramReady]);

  return null;
};

const App: React.FC = () => {
  return (
    <QueryProvider>
      <AppLayout>
        <TelegramIntegration />
        <AppRouter />
      </AppLayout>
    </QueryProvider>
  );
};

export default App;
